import React, { useCallback, memo } from 'react';
import MaterialSelector from '../PricingDetails/MaterialSelector/MaterialSelector';

const MemoizedMaterialSelector = memo(MaterialSelector);

const ComponentSpecRenderer = memo(({
    componentDef,
    items = [],
    onItemsChange,
    readOnly = false
}) => {
    const addItem = useCallback(() => {
        const newItem = {};

        componentDef.fields.forEach(field => {
            if (field.defaultValue !== undefined) {
                newItem[field.name] = field.defaultValue;
            }
        });

        onItemsChange([...items, newItem]);
    }, [componentDef.fields, items, onItemsChange]);

    const updateItem = useCallback((index, fieldName, value) => {
        const updatedItems = [...items];
        updatedItems[index] = { ...updatedItems[index], [fieldName]: value };
        onItemsChange(updatedItems);
    }, [items, onItemsChange]);

    const removeItem = useCallback((index) => {
        const updatedItems = items.filter((_, i) => i !== index);
        onItemsChange(updatedItems);
    }, [items, onItemsChange]);

    const handleMaterialSelect = useCallback((index, field, material) => {
        const updatedItems = [...items];

        if (material) {
            updatedItems[index]._material = material;
            updatedItems[index].materialId = material._id;
            updatedItems[index].materialName = material.name;
            updatedItems[index].materialCode = material.code;
            updatedItems[index].unit = material.unit;
        } else {
            updatedItems[index]._material = null;
            updatedItems[index].materialId = null;
            updatedItems[index].materialName = null;
            updatedItems[index].materialCode = null;
        }

        onItemsChange(updatedItems);
    }, [items, onItemsChange]);

    const renderField = useCallback((field, item, index) => {
        const value = item[field.name];

        switch (field.type) {
            case 'SELECT':
                return (
                    <select
                        className="form-select form-select-sm"
                        value={value || ''}
                        onChange={(e) => updateItem(index, field.name, e.target.value)}
                        disabled={readOnly}
                    >
                        <option value="">Select {field.label}</option>
                        {field.options?.map(opt => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label || opt.value}
                            </option>
                        ))}
                    </select>
                );

            case 'MATERIAL_SELECT':
                const materialValue = item._material ||
                    item.material ||
                    (item.materialId ? { _id: item.materialId } : null);
                return (
                    <MemoizedMaterialSelector
                        categorySlug={field.rawMaterialCategorySlug}
                        value={materialValue}
                        onChange={(material) => handleMaterialSelect(index, field, material)}
                        placeholder={`Select ${field.label}`}
                        displayFields={['name', 'code', 'vendorProperties.bookName', 'vendorProperties.designOrQuality', 'unit']}
                        disabled={readOnly}
                        filters={{}}
                    />
                );

            case 'NUMBER':
                return (
                    <input
                        type="number"
                        className="form-control form-control-sm"
                        value={value !== undefined && value !== null ? value : ''}
                        onChange={(e) => {
                            const newValue = e.target.value === '' ? null : parseFloat(e.target.value);
                            updateItem(index, field.name, newValue);
                        }}
                        placeholder={field.placeholder}
                        disabled={readOnly}
                    />
                );

            case 'TEXT':
            case 'TEXTAREA':
                const InputComponent = field.type === 'TEXTAREA' ? 'textarea' : 'input';
                return (
                    <InputComponent
                        className={`form-control form-control-sm ${field.type === 'TEXTAREA' ? '' : ''}`}
                        value={value || ''}
                        onChange={(e) => updateItem(index, field.name, e.target.value)}
                        placeholder={field.placeholder}
                        rows={field.type === 'TEXTAREA' ? 2 : 1}
                        disabled={readOnly}
                    />
                );

            case 'BOOLEAN':
                return (
                    <div className="form-check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            checked={value || false}
                            onChange={(e) => updateItem(index, field.name, e.target.checked)}
                            disabled={readOnly}
                        />
                        <label className="form-check-label small">{field.label}</label>
                    </div>
                );

            default:
                return null;
        }
    }, [updateItem, handleMaterialSelect, readOnly]);

    return (
        <div className="component-spec mb-4 border rounded p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h6 className="fw-bold mb-0">{componentDef.label}</h6>
                    {componentDef.description && (
                        <small className="text-muted">{componentDef.description}</small>
                    )}
                </div>
                {componentDef.allowsMultiple && !readOnly && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={addItem}
                    >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add {componentDef.label}
                    </button>
                )}
            </div>

            {items.length === 0 ? (
                <div className="text-center text-muted py-3 bg-light rounded">
                    <small>No {componentDef.label.toLowerCase()} added.</small>
                    {componentDef.allowsMultiple && !readOnly && (
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2"
                            onClick={addItem}
                        >
                            Click to add
                        </button>
                    )}
                </div>
            ) : (
                items.map((item, index) => (
                    <div key={index} className="card mb-2 border bg-light">
                        <div className="card-body p-3">
                            <div className="d-flex justify-content-between mb-2">
                                <strong className="text-primary">
                                    {componentDef.label} #{index + 1}
                                </strong>
                                {!readOnly && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => removeItem(index)}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </button>
                                )}
                            </div>
                            <div className="row">
                                {componentDef.fields.map((field, fieldIndex) => (
                                    <div key={fieldIndex} className={`mb-2 ${field.width || 'col-md-6'}`}>
                                        <label className="form-label small fw-medium">
                                            {field.label}
                                            {field.required && <span className="text-danger ms-1">*</span>}
                                            {field.unit && <span className="text-muted ms-1">({field.unit})</span>}
                                        </label>
                                        {renderField(field, item, index)}
                                    </div>
                                ))}
                            </div>
                            {item._material && (
                                <div className="mt-2 pt-2 border-top">
                                    <small className="text-muted">
                                        <strong>Selected:</strong> {item._material.name}
                                        {item._material.code && ` (${item._material.code})`}
                                        {item._material.unit && ` - Unit: ${item._material.unit}`}
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
});

export default ComponentSpecRenderer;